<template>
  <main v-if="unit">
    <!-- sits inside the Figma "Select a Unit" card: no own border -->
    <div class="unitSelection !border-0 !p-0">
      <div class="flex">
        <button v-if="currentIndex != '0' && !unit.id" type="button"
          class="ml-auto mb-[10px] flex items-center gap-[6px] bg-transparent border-0 p-0 font-roboto text-[13px] font-medium text-[#EE5851]"
          @click="deleteButton()">
          <svg width="12" height="13" viewBox="0 0 12 13" fill="none" aria-hidden="true">
            <path
              d="M1.5 3.2h9M4.4 3.2V2a.8.8 0 0 1 .8-.8h1.6a.8.8 0 0 1 .8.8v1.2M2.6 3.2l.5 7.9a1 1 0 0 0 1 .9h3.8a1 1 0 0 0 1-.9l.5-7.9"
              stroke="#EE5851" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          Delete this Unit
        </button>
      </div>
      <!-- agreement is shown as the "Service Agreement" chip in the card header (Figma) -->
      <!-- sections: Add Unit modal below laptop (Figma mobile 5908-2374) groups the fields under headings -->
      <p v-if="sections"
        class="flex items-center gap-[8px] mt-[6px] mb-[12px] text-[15px] font-semibold font-roboto text-[#2E3138]">
        <span class="w-[3px] h-[16px] rounded-full bg-[#6C79E0]" />Unit Details</p>
      <div class="row">
        <div :class="'col-lg-4 col-md-6 col-sm-6 col-12 ' + (errorObject['unit_name'] ? 'has-error' : '')">
          <div :id="currentIndex + '-unit_name'" class="form-group mb-3 text-start">
            <label class="mb-1">Unit Name <span class="text-[#EE5851]">*</span></label>
            <input v-model="unit.unit_name" type="text" class="form-control" placeholder="Unit">
            <small v-if="errorObject['unit_name']" class="unitFieldError">Unit name is required</small>
          </div>
        </div>
        <div :class="'col-lg-4 col-md-6 col-sm-6 col-12 ' + (errorObject['manufacturer'] ? 'has-error' : '')">
          <div :id="currentIndex + '-manufacturer'" class="form-group mb-3 text-start">
            <label class="mb-1">Manufacturer <span class="text-[#EE5851]">*</span></label>
            <FxSelect v-model="selectedManufacture" :options="preConfig?.manufacturers || []" label-key="title"
              value-key="object" :button-class="selectErrorClass('manufacturer')"
              @change="onChange('manufacturer', selectedManufacture.id)" />
            <small v-if="errorObject['manufacturer']" class="unitFieldError">Manufacturer is required</small>
          </div>
        </div>
        <div :class="'col-lg-4 col-md-6 col-sm-6 col-12 ' + (errorObject['manufacture_date'] ? 'has-error' : '')">
          <div :id="currentIndex + '-manufacture_date'" class="form-group mb-3 text-start">
            <label class="mb-1">Manufacture Date <span class="text-[#EE5851]">*</span></label>
            <date-picker v-model:value="unit.manufacture_date" input-class="disable-input" format="MM/DD/YYYY"
              :placeholder="'Select Date'" :confirm="true" :readonly="false" :clearable="false"
              :disabled-date="disabledAfter" value-type="format" :append-to-body="false" />
            <small v-if="errorObject['manufacture_date']" class="unitFieldError">Manufacture date is required</small>
          </div>
        </div>
        <div v-if="sections" class="col-12">
          <p v-if="sections"
            class="flex items-center gap-[8px] mt-[6px] mb-[12px] text-[15px] font-semibold font-roboto text-[#2E3138]">
            <span class="w-[3px] h-[16px] rounded-full bg-[#6C79E0]" />System Specification</p>
        </div>
        <div :class="'col-lg-4 col-md-6 col-sm-6 col-12 ' + (errorObject['system_type_id'] ? 'has-error' : '')">
          <div :id="currentIndex + '-system_type_id'" class="form-group mb-3 text-start">
            <label class="mb-1">System Type <span class="text-[#EE5851]">*</span></label>
            <FxSelect v-model="selectedSystemType" :options="preConfig?.system_types || []" label-key="name"
              value-key="object" :button-class="selectErrorClass('system_type_id')"
              @change="onChange('system_type_id', selectedSystemType.id)" />
            <small v-if="errorObject['system_type_id']" class="unitFieldError">System type is required</small>
          </div>
        </div>
        <div :class="'col-lg-4 col-md-6 col-sm-6 col-12 ' + (errorObject['product_type_id'] ? 'has-error' : '')">
          <div :id="currentIndex + '-product_type_id'" class="form-group mb-3 text-start">
            <label class="mb-1">Product Type <span class="text-[#EE5851]">*</span></label>
            <FxSelect v-model="selectedProductType" :options="selectedSystemType?.product_types || []" label-key="name"
              value-key="object" :disabled="selectedSystemType == null"
              :button-class="selectErrorClass('product_type_id')"
              @change="onChange('product_type_id', selectedProductType.id)" />
            <small v-if="errorObject['product_type_id']" class="unitFieldError">Product type is required</small>
          </div>
        </div>
        <div
          v-if="selectedProductType == null || (selectedProductType != null && selectedProductType.slug != 'cooling')"
          :class="'col-lg-4 col-md-6 col-sm-6 col-12 ' + (errorObject['heat_type_id'] ? 'has-error' : '')">
          <div :id="currentIndex + '-heat_type_id'" class="form-group mb-3 text-start">
            <label class="mb-1">Heat Type <span class="text-[#EE5851]">*</span></label>
            <FxSelect v-model="selectedHeatType" :options="selectedProductType?.heat_types || []" label-key="value"
              value-key="object" :disabled="selectedProductType == null"
              :button-class="selectErrorClass('heat_type_id')"
              @change="onChange('heat_type_id', selectedHeatType.id)" />
            <small v-if="errorObject['heat_type_id']" class="unitFieldError">Heat type is required</small>
          </div>
        </div>
      </div>

      <!-- top options -->
      <div v-if="
        configData[currentIndex] != null &&
        configData[currentIndex].questions_top
      " class="row">
        <div v-for="(item, index) in configData[currentIndex].questions_top
          .data" :key="index" :class="'col-lg-' +
            item.field_size +
            ' col-md-6' +
            item.field_size +
            ' col-sm-6 col-12' +

            (errorObject[item.name] ? ' has-error' : '')
            ">
          <div v-if="isFieldsShow(item)">
            <div v-if="item.type == 'dropdown'" :id="currentIndex + '-' + item.name" class="form-group mb-3 text-start">
              <label class="mb-1"> {{ item.label }} <span v-if="isRequired(item.name)"
                  class="text-[#EE5851]">*</span></label>
              <FxSelect v-model="unit[item.name]" :options="item.options || []" label-key="title" value-key="id"
                :button-class="selectErrorClass(item.name)" />
              <small v-if="errorObject[item.name]" class="unitFieldError">{{ item.label }} is required</small>
            </div>

            <div v-else-if="item.type == 'text'" :id="currentIndex + '-' + item.name"
              class="form-group mb-3 text-start">
              <label class="mb-1"> {{ item.label }} <span v-if="isRequired(item.name)"
                  class="text-[#EE5851]">*</span></label>
              <input v-model="unit[item.name]" type="text" class="form-control" :placeholder="'Enter ' + item.label"
                @paste="disablePaste(item)" @keypress="isNumber($event, item)" @input="isNumber($event, item)">
              <small v-if="errorObject[item.name]" class="unitFieldError">{{ item.label }} is required</small>
            </div>

            <div v-else-if="item.type == 'rich_text'" :id="currentIndex + '-' + item.name"
              class="form-group mb-3 text-start">
              <label class="mb-1"> {{ item.label }} <span v-if="isRequired(item.name)"
                  class="text-[#EE5851]">*</span></label>
              <textarea v-model="unit[item.name]" type="text" class="form-control" :placeholder="'Enter ' + item.label"
                @paste="disablePaste(item)" @keypress="isNumber($event, item)" @input="isNumber($event, item)" />
            </div>

            <div v-else-if="item.type == 'radio'" :id="currentIndex + '-' + item.name"
              class="form-group mb-3 text-start">
              <h6 class="mb-3">
                {{ item.label }} <span v-if="isRequired(item.name)" class="text-[#EE5851]">*</span>
              </h6>
              <div v-if="item != null" class="radioCheck d-flex mt-3">
                <div v-for="(opt, idx) in item.options" :key="idx" class="form-check p-0 me-3">
                  <input :id="item.name + currentIndex + opt.title" v-model="unit[item.name]" type="radio"
                    :value="opt.id" :name="item.name + '-' + currentIndex">
                  <label :for="item.name + currentIndex + opt.title">{{
                    opt.title
                  }}</label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- middle options -->
      <div v-if="configData[currentIndex] != null">
        <p v-if="sections && configData[currentIndex].outdoor"
          class="flex items-center gap-[8px] mt-[6px] mb-[12px] text-[15px] font-semibold font-roboto text-[#2E3138]">
          <span class="w-[3px] h-[16px] rounded-full bg-[#6C79E0]" />Equipment Identification</p>
        <div v-if="
          configData[currentIndex] != null &&
          configData[currentIndex].indoor &&
          configData[currentIndex].outdoor
        " class="row">
          <div v-for="side in ['outdoor', 'indoor']" :key="side" class="col-lg-6 col-md-6 col-sm-12 col-12">
            <div class="outdoorUnit">
              <div class="outdoorHead">
                {{ side == 'outdoor' ? 'Outdoor Unit' : 'Indoor Unit' }}
              </div>
              <!-- Figma: scan buttons centred under the bar, serial full width below -->
              <div class="serialBarcode">
                <img src="../../assets/images/barcode-icon.png" alt="Scan barcode"
                  @click="openModal(side + '_serial', 'barcode')">
                <img src="../../assets/images/qrcode-icon.png" alt="Scan QR code"
                  @click="openModal(side + '_serial', 'qrcode')">
              </div>
              <div class="form-group text-start">
                <label class="mb-1">Serial Number</label>
                <input v-model="unit[side + '_serial']" type="text" class="form-control" placeholder="Enter serial">
              </div>
              <div class="form-group text-start">
                <label class="mb-1">Model Number</label>
                <input v-model="unit[side + '_model']" type="text" class="form-control" placeholder="Enter model">
              </div>
            </div>
          </div>
        </div>
        <div v-else-if="
          configData[currentIndex] != null && configData[currentIndex].outdoor
        ">
          <div class="outdoorUnit">
            <div class="outdoorHead">
              Outdoor Unit
            </div>
            <div class="serialBarcode">
              <img src="../../assets/images/barcode-icon.png" alt="Scan barcode"
                @click="openModal('outdoor_serial', 'barcode')">
              <img src="../../assets/images/qrcode-icon.png" alt="Scan QR code"
                @click="openModal('outdoor_serial', 'qrcode')">
            </div>
            <div class="row">
              <div class="col-lg-6 col-md-6 col-sm-12 col-12">
                <div class="form-group text-start">
                  <label class="mb-1">Serial Number</label>
                  <input v-model="unit['outdoor_serial']" type="text" class="form-control" placeholder="Enter serial">
                </div>
              </div>
              <div class="col-lg-6 col-md-6 col-sm-12 col-12">
                <div class="form-group text-start">
                  <label class="mb-1">Model Number</label>
                  <input v-model="unit['outdoor_model']" type="text" class="form-control" placeholder="Enter model">
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- bottom options -->
      <p v-if="sections && configData[currentIndex]?.questions_bottom"
        class="flex items-center gap-[8px] mt-[6px] mb-[12px] text-[15px] font-semibold font-roboto text-[#2E3138]">
        <span class="w-[3px] h-[16px] rounded-full bg-[#6C79E0]" />Additional Information</p>
      <div v-if="
        configData[currentIndex] != null &&
        configData[currentIndex].questions_bottom
      " class="row">
        <div v-for="(item, index) in configData[currentIndex].questions_bottom
          .data" :key="index" :class="'col-lg-' +
            item.field_size +
            ' col-md-' +
            item.field_size +
            ' col-sm-12 col-12' +
            (errorObject[item.name] ? ' has-error' : '')
            ">
          <div v-if="item.type == 'dropdown'" :id="currentIndex + '-' + item.name" class="form-group mb-3 text-start">
            <label class="mb-1"> {{ item.label }} <span v-if="isRequired(item.name)"
                class="text-[#EE5851]">*</span></label>
            <FxSelect v-model="unit[item.name]" :options="item.options || []" label-key="title" value-key="id"
              :button-class="selectErrorClass(item.name)" />
            <small v-if="errorObject[item.name]" class="unitFieldError">{{ item.label }} is required</small>
          </div>

          <div v-else-if="item.type == 'text'" :id="currentIndex + '-' + item.name" class="form-group mb-3 text-start">
            <label class="mb-1"> {{ item.label }} <span v-if="isRequired(item.name)"
                class="text-[#EE5851]">*</span></label>
            <input v-model="unit[item.name]" type="text" class="form-control" :placeholder="'Enter ' + item.label"
              @paste="disablePaste(item)" @keypress="isNumber($event, item)" @input="isNumber($event, item)">
            <small v-if="errorObject[item.name]" class="unitFieldError">{{ item.label }} is required</small>
          </div>

          <div v-else-if="item.type == 'rich_text'" :id="currentIndex + '-' + item.name"
            class="form-group mb-3 text-start">
            <label class="mb-1"> {{ item.label }} <span class="textareaLabelDesktop">(Notes cannot exceed 500
                characters.)</span></label>
            <textarea v-model="unit[item.name]" type="text" class="form-control" maxlength="500"
              :placeholder="'Enter ' + item.label" @paste="disablePaste(item)" @keypress="isNumber($event, item)"
              @input="isNumber($event, item)" />
            <span class="textareaLabelMobile">(Notes cannot exceed 500 characters.)</span>
          </div>

          <div v-else-if="item.type == 'radio'" :id="currentIndex + '-' + item.name" class="form-group mb-3 text-start">
            <h6 class="mb-3">
              {{ item.label }} <span v-if="isRequired(item.name)" class="text-[#EE5851]">*</span>
            </h6>

            <div class="radioCheck d-flex mt-3">
              <div v-for="(opt, idx) in item.options" :key="idx" class="form-check p-0 me-3">
                <input :id="opt.id + item.name + currentIndex" v-model="unit[item.name]" type="radio" :value="opt.id"
                  :name="item.name + '-' + currentIndex">
                <label :for="opt.id + item.name + currentIndex">{{
                  opt.title
                }}</label>
              </div>
            </div>
          </div>
        </div>
      </div>
      <button v-if="currentIndex != '0' && !unit.id" type="button" class="fxBtnSecondary" @click="resetBtn">
        Reset
      </button>
    </div>
  </main>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import DatePicker from "vue-datepicker-next";
import FxSelect from "../partialComponent/FxSelect.vue";

import _ from "lodash";
export default {
  components: {
    DatePicker,
    FxSelect,
  },

  computed: {
    ...mapGetters(["unitsList", "apiCallback", "preConfig", "config"]),

    isMobile() {
      return window.screen.width < 1024 ? false : true;
    },
  },

  props: ["carouselSlideIndex", "item", "currentIndex", "configData", "units", "errorObject", "sections"],

  data() {
    return {
      selectedManufacture: null,
      selectedSystemType: null,
      selectedProductType: null,
      selectedHeatType: null,
      isPresetSystemSet: false,
      isPresetProductSet: false,
      isPresetHeatSet: false,
      jobId: this.$route.params.id,
      unit: this.item,
      count: -1,
    };
  },
  watch: {
    selectedSystemType: {
      handler: function () {
        if (!this.isPresetSystemSet) {
          this.selectedProductType = null;
          this.selectedHeatType = null;
          this.configData[this.currentIndex] = null;
          this.resetUnitConfig()
        } else {
          this.isPresetSystemSet = false;
        }
      },
    },

    selectedProductType: {
      handler: function () {
        if (!this.isPresetProductSet) {
          this.selectedHeatType = null;
          this.configData[this.currentIndex] = null;
          this.resetUnitConfig()

          if (this.selectedProductType != null && this.selectedProductType.slug == 'cooling') {
            this.getConfig({
              system_type_id: this.selectedSystemType.id,
              product_type_id: this.selectedProductType.id,
            });
          }

        } else {
          this.isPresetProductSet = false;
        }
      },
    },

    selectedHeatType: {
      handler: function () {
        if (!this.isPresetHeatSet) {
          this.configData[this.currentIndex] = null;
          if (this.selectedHeatType != null) {
            this.resetUnitConfig()
            this.getConfig({
              system_type_id: this.selectedSystemType.id,
              product_type_id: this.selectedProductType.id,
              heat_type_id: this.selectedHeatType.id,
            });
          }
        } else {
          this.isPresetHeatSet = false;
        }
      },
    },

    carouselSlideIndex: {
      handler() {
        if (this.preConfig.manufacturers) {
          this.selectedManufacture = null;
          this.selectedSystemType = null;
          this.selectedProductType = null;
          this.selectedHeatType = null;
          this.isPresetSystemSet = false;
          this.isPresetProductSet = false;
          this.isPresetHeatSet = false;
          this.unit = this.units[this.carouselSlideIndex];
          this.setData();
        }

      },
      deep: true,
      immediate: true
    },


    config: function () {
      this.configData[this.currentIndex] = this.config;
    },

    preConfig: function () {
      this.unit = this.units[this.currentIndex];
      this.setData();
    },
  },

  mounted() {

  },

  methods: {
    ...mapActions(["getPreConfig", "getConfig"]),

    onChange(key, value) {
      if (key == 'product_type_id') {
        this.unit['is_cooling_only'] = this.selectedProductType.slug == 'cooling' ? 1 : 0
      }
      this.unit[key] = value;
    },
    // same rule as isValid in select-unit.vue: every config question is required except these
    isRequired(name) {
      return !["notes", "coil_model"].includes(name);
    },
    isFieldsShow(data) {
      if (
        data.name == "new_capacity" &&
        this.unit.is_system_sized_properly != 0
      ) {
        return false;
      } else {
        return true;
      }
    },

    setData() {
      let self = this;

      this.selectedManufacture =
        this.preConfig.manufacturers.filter(function (item) {
          return (
            self.unit["manufacturer"] != null &&
            item.id == self.unit["manufacturer"]
          );
        })[0] ?? null;

      this.isPresetSystemSet = true;
      this.selectedSystemType =
        this.preConfig.system_types.filter(function (item) {
          return (
            self.unit["system_type_id"] != null &&
            item.id == self.unit["system_type_id"]
          );
        })[0] ?? null;

      if (this.selectedSystemType != null) {
        this.isPresetProductSet = true;
        this.selectedProductType =
          this.selectedSystemType.product_types.filter(function (item) {
            return (
              self.unit["product_type_id"] != null &&
              item.id == self.unit["product_type_id"]
            );
          })[0] ?? null;
      } else {
        this.selectedProductType = null;
      }

      if (this.selectedProductType != null && this.selectedProductType.slug != 'cooling') {
        this.isPresetHeatSet = true;
        this.selectedHeatType =
          this.selectedProductType.heat_types.filter(function (item) {
            return (
              self.unit["heat_type_id"] != null &&
              item.id == self.unit["heat_type_id"]
            );
          })[0] ?? null;
      } else {
        this.selectedHeatType = null;
      }

      if (this.selectedProductType != null && this.selectedProductType.slug == 'cooling') {
        this.getConfig({
          system_type_id: this.selectedSystemType.id,
          product_type_id: this.selectedProductType.id,
        });
      } else if (this.selectedHeatType != null) {
        this.getConfig({
          system_type_id: this.selectedSystemType.id,
          product_type_id: this.selectedProductType.id,
          heat_type_id: this.selectedHeatType.id,
        });
      }

    },

    // Figma "Select a Unit — Validation Errors" look for an FxSelect (same as .has-error .form-select)
    selectErrorClass(key) {
      return this.errorObject[key] ? "!border-[1.5px] !border-[#F67280] !bg-[#FEF7F7]" : "";
    },

    disabledAfter: function (date) {
      const cdate = new Date();
      cdate.setHours(0, 0, 0, 0);
      return date > cdate;
    },

    saveMoreUnit() { },

    jobDetailClicked() {
      this.$router.go(-1);
    },

    disablePaste(item, event) {
      if (item.validation == "float") event.preventDefault();
    },

    isNumber: function (evt, item) {
      if (item.validation == "float") {
        const inputElement = evt.target;
        let value = inputElement.value;

        if (evt.type === 'input') {
          value = value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
          const [integer, decimal] = value.split('.');
          if (integer.length > 4) value = integer.slice(0, 4);
          if (decimal) {
            if (integer === "") {
              value = "";
            } else {
              value = `${integer}.${decimal.slice(0, 2)}`;
            }
          }
          if (value.startsWith('.') || value === '') {
            value = '';
          }
          // re-dispatch only on a change, otherwise this @input handler calls itself forever (QA D-46)
          if (inputElement.value !== value) {
            inputElement.value = value;
            inputElement.dispatchEvent(new Event('input', { bubbles: true }));
          }
        } else if (evt.type === 'keypress') {
          const charCode = evt.which || evt.keyCode;
          console.log("charCode", charCode);

          if (charCode === 8 || charCode === 46 || charCode === 9 || (charCode >= 48 && charCode <= 57)) {
            if (charCode === 46 && value.indexOf('.') !== -1) {
              evt.preventDefault();
              return false;
            } else {
              // Remove invalid characters and multiple decimal points
              value = value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
              const [integer, decimal] = value.split('.');

              // Restrict length
              if (integer.length > 3) value = integer.slice(0, 3);
              if (decimal) value = value.split('.')[0] + '.' + decimal.slice(0, 1);

              inputElement.value = value;
              return true;
            }

          }
          evt.preventDefault();
          return false;
        }

      }
    },

    resetBtn() {
      this.selectedManufacture = null;
      this.selectedSystemType = null;
      this.selectedProductType = null;
      this.selectedHeatType = null;

      for (let k in this.unit) {
        this.unit[k] = null
      }
    },


    resetUnitConfig() {
      let config = this.config
      for (var key in config) {
        if (config.hasOwnProperty(key)) {
          for (var indx in config[key].data) {
            let dataItems = config[key].data[indx];
            let dataKey = dataItems.name;
            this.units[this.currentIndex][dataItems.name] = null
          }
        }
      }
    },

    deleteButton() {
      this.$emit("event", "delete", this.currentIndex);
    },

    openModal(data, type) {
      this.$emit("openModalQR", data, type);
    },
  },
};
</script>

<style>
/* text inputs' placeholder grey like the dropdowns' "Select" (an older global rule makes it #333) */
.unitSelection .form-control::placeholder {
  color: #aaaaaa !important;
}

/* date popup renders under its input (not on <body>, where it opened far from the field in the Add Unit modal);
   the library still positions it from the viewport, so pin it just below the input */
.unitSelection .mx-datepicker {
  position: relative;
}

.unitSelection .mx-datepicker-popup {
  top: 100% !important;
  left: 0 !important;
  bottom: auto !important;
  margin-top: 4px;
  z-index: 20;
}

.carousel__slide {
  display: table !important;
}

.unitSelection {
  width: 85% !important;
  margin: 0 auto;
}

.serialBarcode img {
  width: 60px;
  height: 60px;
}

.has-error input,
.has-error select {
  border-color: red;
}

.textareaLabelDesktop,
.textareaLabelMobile {
  color: red;
  font-size: 12px;
}

.textareaLabelMobile {
  display: none;
}

@media screen and (max-device-width: 576px) {
  .textareaLabelDesktop {
    display: none;
  }

  .textareaLabelMobile {
    display: block !important;
    margin-top: 0.5rem;
  }
}
</style>
